import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanySpecificService, CreateCompanySpecificService, CurrencyCode } from "@/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import {
  CreateCompanySpecificServiceFormInput,
  CreateCompanySpecificServiceValidationSchema,
} from "./validation/createCompanySpecificServiceValidationSchema";
import { SettingsFormContent, SettingsFormCTAs, SettingsHeader } from "./index";
import { Grid, Stack } from "@mantine/core";

import Heading from "@/components/atoms/Heading";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import NumberInput from "@/components/atoms/NumberInput";
import Group from "@/components/atoms/Group";
import ServiceTable from "../../organisms/SettingsTables/ServiceTable";
import { postCreateCompanySpecificService, useGetCompanySpecificServices } from "@/services/Company";
import TextInput from "@/components/atoms/TextInput";
import { notifications } from "@mantine/notifications";
import SelectPaymentFrequency from "@/components/molecules/SelectInputs/SelectPaymentFrequency";
import { useAuth } from "@/contexts/AuthContext";
import SelectCurrency from "@/components/molecules/SelectInputs/SelectCurrency";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";

export const Services: React.FC = () => {
  const [selectedService, setSelectedService] = useState<CompanySpecificService | null>(null);
  const [includeDisabled, setIncludeDisabled] = useState<boolean>(false);
  const { t } = useTranslation();

  const { currentCompany, isAdminUser } = useAuth();
  const isAdmin = isAdminUser(currentCompany);
  const { data: companySpecificServices } = useGetCompanySpecificServices(
    currentCompany?.companyId as number,
    includeDisabled,
    {
      enabled: !!currentCompany?.companyId,
    }
  );

  return (
    <Stack data-testid="service-settings">
      <Heading order={2} styleOrder={4} tt="uppercase">
        {t("settings:navigation.services")}
      </Heading>
      <ServiceForm
        companyId={currentCompany?.companyId}
        selectedService={selectedService}
        setSelectedService={setSelectedService}
      />
      <ServiceTable
        services={companySpecificServices || []}
        currentCompanyId={currentCompany?.companyId}
        isAdmin={isAdmin}
        setSelectedService={setSelectedService}
        includeDisabled={includeDisabled}
        setIncludeDisabled={setIncludeDisabled}
      />
    </Stack>
  );
};

interface ServiceFormProps {
  companyId: number | undefined;
  selectedService: CompanySpecificService | null;
  setSelectedService: React.Dispatch<React.SetStateAction<CompanySpecificService | null>>;
}

const ServiceForm = ({ companyId, selectedService, setSelectedService }: ServiceFormProps) => {
  const queryClient = useQueryClient();
  const { currentCompany } = useAuth();
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<CreateCompanySpecificServiceFormInput>({
    resolver: zodResolver(CreateCompanySpecificServiceValidationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    values: {
      companyId: companyId as number,
      name: selectedService?.name || "",
      price: selectedService?.price || 0,
      currency: selectedService?.currency || (currentCompany?.companyCurrency as string),
      servicePriceUnitId: selectedService?.priceUnit?.id.toString() || "1",
    },
  });

  const { mutateAsync: createService, isPending } = useMutation({
    mutationFn: async (data: CreateCompanySpecificService) => {
      return await postCreateCompanySpecificService(data);
    },
    onMutate: async (data: CreateCompanySpecificService) => {
      const queryKey = ["getCompanySpecificServices", companyId];
      await queryClient.cancelQueries({ queryKey });
      const snapshots = queryClient.getQueriesData<CompanySpecificService[]>({ queryKey });
      const optimisticService = {
        id: -Date.now(),
        companyId: data.companyId,
        name: data.name,
        price: data.price,
        currency: data.currency,
        priceUnit: { id: data.servicePriceUnitId } as CompanySpecificService["priceUnit"],
      };
      queryClient.setQueriesData<CompanySpecificService[]>({ queryKey }, (old) =>
        old ? [...old, optimisticService] : old
      );
      return { snapshots };
    },
    onSuccess: () => {
      notifications.show(
        SuccessNotificationObj(
          t("notifications:generic.success"),
          t("notifications:settings.createService"),
          "service-created-success"
        )
      );
      queryClient.invalidateQueries({
        queryKey: ["getCompanySpecificServices", companyId],
      });
    },
    onError: (_err, _data, context) => {
      context?.snapshots?.forEach(([queryKey, data]) => queryClient.setQueryData(queryKey, data));
      notifications.show(
        ErrorNotificationObj(
          t("notifications:generic.error"),
          t("notifications:settings.createServiceError"),
          "service-created-error"
        )
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCompanySpecificServices", companyId],
      });
    },
  });

  const onSubmit = async (data: CreateCompanySpecificServiceFormInput) => {
    const request: CreateCompanySpecificService = {
      ...data,
      servicePriceUnitId: Number(data.servicePriceUnitId),
      currency: data.currency.toUpperCase() as CurrencyCode,
    };
    await createService(request);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <SettingsFormContent>
        <Grid>
          <SettingsHeader>
            <Text size="md" strong={true} display="technical" tt="uppercase">
              {t("settings:serviceSettings.addCompanySpecificService")}
            </Text>
          </SettingsHeader>
          <Grid.Col span={{ base: 12, md: 6, lg: 5 }}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                return (
                  <TextInput
                    {...field}
                    id="name"
                    data-testid="service-name-input"
                    label={t("common:name")}
                    value={field.value || ""}
                    disabled={!!selectedService}
                    editable={!selectedService}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                  />
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 7 }}>
            <Group align="flex-start" m={0} p={0} w={"100%"} gap={0} wrap={"nowrap"}>
              <Controller
                control={control}
                name="price"
                render={({ field, fieldState }) => {
                  return (
                    <NumberInput
                      {...field}
                      id="price"
                      value={field.value || ""}
                      label={t("common:price")}
                      disabled={!!selectedService}
                      editable={!selectedService}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                      hideControls={true}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="currency"
                render={({ field, fieldState }) => {
                  return (
                    <SelectCurrency
                      {...field}
                      labelKey={"value"}
                      valueKey={"value"}
                      label={t("common:currency")}
                      disabled={!!selectedService}
                      editable={!selectedService}
                      value={field.value || ""}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 5 }}>
            <Controller
              control={control}
              name="servicePriceUnitId"
              render={({ field, fieldState }) => {
                return (
                  <SelectPaymentFrequency
                    id="servicePriceUnitId"
                    label={t("settings:paymentFrequency")}
                    labelKey={"code"}
                    valueKey={"id"}
                    disabled={!!selectedService}
                    editable={!selectedService}
                    companyId={companyId}
                    error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    {...field}
                  />
                );
              }}
            />
          </Grid.Col>
        </Grid>
      </SettingsFormContent>
      <SettingsFormCTAs>
        <CTA
          disabled={!selectedService}
          id={"reset"}
          size="lg"
          intent="secondary"
          onClick={() => {
            reset();
            setSelectedService(null);
          }}
        >
          {t("actions:hide")}
        </CTA>
        <CTA
          disabled={!isValid || !!selectedService}
          id="save"
          data-testid="submit-btn"
          size="lg"
          type="submit"
          loading={isPending}
        >
          {t("actions:save")}
        </CTA>
      </SettingsFormCTAs>
    </form>
  );
};

export default Services;
